import type { ComputedRef, Ref } from "vue";
import { computed, ref } from "vue";
import {
  DSSelectNormalizedModelValue,
  DSSelectNormalizedProps,
} from "@/src/design_system/form_elements/select/types/props";
import { DSSelectEmits } from "@/src/design_system/form_elements/select/types/emits";
import { useSelectPopover } from "@/src/design_system/form_elements/select/composables/useSelectPopover";
import { useSelectSelection } from "@/src/design_system/form_elements/select/composables/useSelectSelection";
import { useSelectSearch } from "@/src/design_system/form_elements/select/composables/useSelectSearch";
import { useSelectInputProps } from "@/src/design_system/form_elements/select/composables/useSelectInputProps";
import { useSelectKeyboardNavigation } from "@/src/design_system/form_elements/select/composables/useSelectKeyboardNavigation";
import { useSelectFocusInput } from "@/src/design_system/form_elements/select/composables/useSelectFocusInput";
import { useSelectClearSelection } from "@/src/design_system/form_elements/select/composables/useSelectClearSelection";
import { useSelectArrowNavigation } from "@/src/design_system/form_elements/select/composables/useSelectArrowNavigation";
import { useSelectEnter } from "@/src/design_system/form_elements/select/composables/useSelectEnter";
import { useSelectTypeaheadKeydown } from "@/src/design_system/form_elements/select/composables/useSelectTypeaheadKeydown";
import { DSBaseListOption, DSOptionListModelValue } from "@/src/design_system/form_elements/option_list/types";
import { DSBaseInputProps } from "@/src/design_system/form_elements/base_input/types/dsBaseInput";
import { DSOptionValue } from "@/src/design_system/form_elements/shared/types/option";
import { useInputPopoverInteractions } from "@/src/design_system/form_elements/shared/composables/useInputPopoverInteractions";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import { VueComponentTemplateRef } from "@/src/shared/types/vueTemplateRef";
import { flattenOptions } from "@/src/design_system/form_elements/option_list/utils/optionListUtils";

export interface UseSelect {
  isOpen: Ref<boolean>;
  selectedValues: Ref<DSOptionValue[]>;
  selectedOptions: ComputedRef<DSBaseListOption[]>;
  displayValue: ComputedRef<string>;
  removeOption: (value?: DSOptionValue) => void;
  showSelectedTags: ComputedRef<boolean>;
  optionListModelValue: Ref<DSOptionListModelValue>;
  onBackspace: () => void;
  onInputFocus: (event?: FocusEvent) => void;
  onInputBlur: () => void;
  baseInputModelValue: Ref<string | null>;
  baseInputProps: ComputedRef<DSBaseInputProps>;
  togglePopover: () => void;
  clearSelection: () => void;
  clearSearch: () => void;
  onInputClick: (e: MouseEvent) => void;
  onSuffixClick: () => void;
  onTab: () => void;
  onEnter: () => void;
  onKeydown: (e: KeyboardEvent) => void;
  listId: string;
  activeOptionId: Ref<string | undefined>;
  onArrowDown: (e: KeyboardEvent) => void;
  onArrowUp: (e: KeyboardEvent) => void;
}

let selectIdCounter = 0;

export const useSelect = (
  modelValue: Ref<DSSelectNormalizedModelValue>,
  emit: DSSelectEmits,
  props: ComputedRef<Readonly<DSSelectNormalizedProps>>,
  baseInputRef: VueComponentTemplateRef<typeof DSBaseInput>
): UseSelect => {
  const listId = `ds-select-listbox-${++selectIdCounter}`;
  const activeOptionId = ref<string | undefined>(undefined);
  const flatOptions = computed<DSBaseListOption[]>(() => flattenOptions(props.value.options));

  const { focusBaseInput } = useSelectFocusInput(baseInputRef);

  const popover = useSelectPopover(props);

  const selection = useSelectSelection({
    modelValue,
    props,
    onSingleSelect: () => {
      popover.close();
      search.clearSearch();
      focusBaseInput();
      activeOptionId.value = undefined;
    },
  });

  const search = useSelectSearch({
    props,
    emit,
    displayValue: selection.displayValue,
    selectedOptions: selection.selectedOptions,
    removeOption: selection.removeOption,
    openPopover: popover.open,
  });

  const baseInputProps = useSelectInputProps({
    props,
    suffix: popover.suffix,
    showSelectedTags: selection.showSelectedTags,
    selectedOptions: selection.selectedOptions,
    listId,
    isOpen: popover.isOpen,
    activeOptionId,
  });

  const { clearSelection } = useSelectClearSelection({
    modelValue,
    props,
    emit,
    clearSearch: search.clearSearch,
    activeOptionId,
  });

  const {
    onInputClick,
    onTab,
    onEnter: baseOnEnter,
  } = useInputPopoverInteractions(popover.isOpen, {
    toggleOnClick: false,
  });

  const keyboardNavigation = useSelectKeyboardNavigation({
    baseOptions: selection.baseOptions,
    selectedValues: selection.selectedValues,
    updateSelection: (value: DSOptionValue) => {
      selection.optionListModelValue.value = value;
    },
  });

  const { onEnter } = useSelectEnter({
    isOpen: popover.isOpen,
    activeOptionId,
    flatOptions,
    listId,
    optionListModelValue: selection.optionListModelValue,
    multiple: computed(() => !!props.value.multiple),
    closePopover: popover.close,
    clearSearch: search.clearSearch,
    focusBaseInput,
    baseOnEnter,
  });

  const onSuffixClick = () => {
    if (!popover.isOpen.value) {
      search.onInputFocus();
      focusBaseInput();
    }

    popover.toggle();
  };

  const { onArrowDown, onArrowUp } = useSelectArrowNavigation({
    flatOptions,
    listId,
    activeOptionId,
    isOpen: popover.isOpen,
    openPopover: popover.open,
  });

  const { onKeydown } = useSelectTypeaheadKeydown({
    props,
    onAlphabeticKey: keyboardNavigation.onAlphabeticKey,
  });

  return {
    isOpen: popover.isOpen,
    selectedValues: selection.selectedValues,
    selectedOptions: selection.selectedOptions,
    displayValue: selection.displayValue,
    removeOption: selection.removeOption,
    showSelectedTags: selection.showSelectedTags,
    optionListModelValue: selection.optionListModelValue,
    onBackspace: search.onBackspace,
    onInputFocus: search.onInputFocus,
    onInputBlur: search.onInputBlur,
    baseInputModelValue: search.baseInputModelValue,
    baseInputProps,
    togglePopover: popover.toggle,
    clearSelection,
    clearSearch: search.clearSearch,
    onInputClick,
    onSuffixClick,
    onTab,
    onEnter,
    onKeydown,
    listId,
    activeOptionId,
    onArrowDown,
    onArrowUp,
  };
};
